import { defineComponent as _defineComponent } from 'vue'
import '@/scss/sidebar-menu.scss';
import './SidebarGroupItem.scss';
import { WorkspaceOrder } from '@/@types/WorkspaceOrder';

import githubIcon from '@/assets/img/integrations/github.svg';
import confluenceIcon from '@/assets/img/integrations/confluence.svg';
import gdocsIcon from '@/assets/img/integrations/gdocs.svg';
import SidebarNestedGroupItem from '@/components/layout/sidebars/SidebarGroupItem/SidebarNestedGroupItem.vue';
import MoreButton from '@/components/layout/sidebars/SidebarGroupItem/MoreButton.vue';
import tooltip from '@/services/ui-providers/Tooltip';
import EventBus from '@/EventBus';
import helper from '@/helper';
import notificationService from '@/services/NotificationService';

import { ref, computed } from 'vue';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { WorkspaceView } from '@/@types/WorkspaceView';
import { Workspace } from '@/models/Workspace';
import slugify from 'slugify';

import bitbucketIcon from '@/assets/img/integrations/bitbucket.svg';


export default /*#__PURE__*/_defineComponent({
  __name: 'SidebarGroupItem',
  props: {
    groupNode: null,
    recalculateSidebarHeight: null,
    nested: { default: 1 },
    paragraphProgress: { default: 0 },
    workspace: null,
    workspaceView: null,
    showArchivedArticles: { type: Boolean },
    articleMode: null,
    isMobileView: { type: Boolean },
    isSharedMode: { type: Boolean },
    expanded: null,
    isInsideIntegration: { type: Boolean, default: false }
  },
  emits: ['toggle-expand', 'navigate-to-first-article', 'edit-integration', 'remove-integration'],
  setup(__props: any, { emit }) {

const props = __props as { groupNode: WorkspaceOrder, recalculateSidebarHeight: () => void, nested: number, paragraphProgress: number, workspace: Workspace, workspaceView: WorkspaceView, showArchivedArticles: boolean, articleMode?: ArticleEditMode, isMobileView: boolean, isSharedMode: boolean, expanded: string[], isInsideIntegration: boolean }



const isTitleFocused = ref(false);



const isIntegration = computed(() => props.groupNode.isIntegration === true);

const SOURCE_ICONS: Record<string, string> = {
  GITHUB: githubIcon,
  BITBUCKET: bitbucketIcon,
  CONFLUENCE: confluenceIcon,
  GDRIVE: gdocsIcon,
};

const sourceIconUrl = computed(() => {
  if (!isIntegration.value) return null;
  const key = props.groupNode.sourceType?.toUpperCase() || '';
  return SOURCE_ICONS[key] ?? null;
});

const hasNestedItems = computed(() => props.groupNode.items && props.groupNode.items.length > 0);

const findFirstArticleInGroup = (groupNode: WorkspaceOrder): any | null => {
  if (!groupNode.items || groupNode.items.length === 0) {
    return null;
  }

  for (const item of groupNode.items) {
    if (item.type === 'group') {
      // Recursively search in nested group
      const found = findFirstArticleInGroup(item);
      if (found) return found;
    } else {
      // Found an article - get it from workspaceView
      const article = props.workspaceView.articles?.find((a: any) => a.id === item.value);
      if (article && (!article.isArchived || props.showArchivedArticles)) {
        return article;
      }
    }
  }

  return null;
};

const hasArticlesInGroup = computed(() => {
  const firstArticle = findFirstArticleInGroup(props.groupNode);
  return firstArticle !== null;
});

const collectArticleIds = (node: WorkspaceOrder): string[] => {
  const ids: string[] = [];
  if (!node.items) return ids;
  for (const item of node.items) {
    if (item.type === 'group') {
      ids.push(...collectArticleIds(item));
    } else {
      ids.push(item.value);
    }
  }
  return ids;
};

const isAllChildrenStale = computed(() => {
  const articleIds = collectArticleIds(props.groupNode);
  if (articleIds.length === 0) return false;
  const articles = props.workspaceView.articles || [];
  return articleIds.every((id) => {
    const article = articles.find((a: any) => a.id === id);
    return article && article.syncStatus === 'STALE';
  });
});

const handleToggle = (item: any) => {
  props.recalculateSidebarHeight();
  emit('toggle-expand', item);
};

const isActiveGroup = () => {
  // Check if the current article is within this group
  // This will be implemented when we integrate with WorkspaceSidebar
  return false;
};

const handleTitleFocus = (e: any, title: string) => {
  tooltip.handleFocusIn(e, title);
  isTitleFocused.value = true;
};

const removeTooltips = () => {
  tooltip.clearTooltips();
  isTitleFocused.value = false;
};

const handleClick = (e: PointerEvent | MouseEvent) => {
  e.preventDefault();
  const target = e.target as HTMLElement;
  const isModifierPressed = e.metaKey || e.ctrlKey || e.button === 1;

  const isAlreadyExpanded = props.groupNode && props.expanded.includes(props.groupNode.value);

  if (target.closest('.group-more-button')) {
    return;
  }

  if (target.closest('.article-item-options')) {
    return;
  }

  // If modifier key pressed, navigate to first article in new tab (regardless of expanded state)
  if (isModifierPressed) {
    emit('navigate-to-first-article', { groupNode: props.groupNode, newTab: true });
    return;
  }

  // If already expanded and no modifier key, just collapse
  if (isAlreadyExpanded) {
    handleToggle(props.groupNode);
    return;
  }

  // If collapsed, just expand (don't navigate)
  handleToggle(props.groupNode);
};

const handleRenameGroup = () => {
  EventBus.$emit('rename-group', props.groupNode);
};

const handleUngroup = () => {
  EventBus.$emit('ungroup', props.groupNode);
};

const handleCopyGroupLink = () => {
  if (props.workspace && props.groupNode) {
    const baseUrl = location.href.replace(location.hash, '');

    // Find first article in the group to determine type
    const firstArticle = findFirstArticleInGroup(props.groupNode);

    if (firstArticle) {
      // Determine article type based on content
      let articleType = '';
      if (firstArticle.isHasVideo) {
        articleType = 'video';
      } else if (firstArticle.isHasNotes) {
        articleType = 'notes';
      }

      // Generate article slug from title
      const articleSlug = slugify(firstArticle.title);

      // Format: /b/{workspaceShortId}/{workspaceSlug}/{articleShortId}/{articleSlug}/{type}
      const url = `${baseUrl}#/b/${props.workspace.shortId}/${props.workspace.slug}/${firstArticle.shortId}/${articleSlug}/${articleType}`;
      helper.copyToClipboard(url);
      notificationService.success('Group link copied to clipboard');
    } else {
      // Fallback to workspace URL if no article found
      const url = `${baseUrl}#/b/${props.workspace.shortId}/${props.workspace.slug}`;
      helper.copyToClipboard(url);
      notificationService.success('Group link copied to clipboard');
    }
  } else if (props.isSharedMode) {
    helper.copyToClipboard(location.href);
    notificationService.success('Group link copied to clipboard');
  } else {
    notificationService.warning('Cannot copy link to this group');
  }
};

const createSubArticle = () => {
  EventBus.$emit('create-sub-article-in-group', props.groupNode);
};

return { __sfc: true,props, isTitleFocused, emit, isIntegration, SOURCE_ICONS, sourceIconUrl, hasNestedItems, findFirstArticleInGroup, hasArticlesInGroup, collectArticleIds, isAllChildrenStale, handleToggle, isActiveGroup, handleTitleFocus, removeTooltips, handleClick, handleRenameGroup, handleUngroup, handleCopyGroupLink, createSubArticle, SidebarNestedGroupItem, MoreButton }
}

})